// Données éditoriales statiques : aucune API ni base distante n’est requise.
export type ResourceCategory =
  | "water" | "water_unverified" | "food" | "sleep" | "repair"
  | "train" | "health" | "toilets" | "charge" | "info" | "rest";

export type Stage = {
  id: number;
  from: string;
  to: string;
  distanceKm: number;
  officialTime: string;
  difficulty: "habitué" | "aventure";
  summary: string;
  terrain: string;
  watch: string[];
  stops: string[];
  retreat: string[];
  sleepPlan: string;
  gpx: string;
  checkedAt: string;
};

export type OsmResource = {
  id: string;
  name: string;
  category: ResourceCategory;
  lat: number;
  lon: number;
  stageId: number;
  distanceToRouteKm: number;
  openingHours: string | null;
  phone: string | null;
  website: string | null;
  access: string;
  potable: boolean | null;
  sourceName: string;
  sourceUrl: string;
  verifiedAt: string;
  confidence: string;
  criticality: string;
};

export type SafetySource = {
  id: string;
  label: string;
  area: string;
  cadence: string;
  url: string;
  critical: boolean;
};

export const stages: Stage[] = [
  {
    id: 1, from: "Biarritz", to: "Urt", distanceKm: 40, officialTime: "≈ 2 h 40",
    difficulty: "habitué",
    summary: "De l’Atlantique à l’Adour, puis une sortie progressive de l’agglomération vers les barthes.",
    terrain: "Voies cyclables urbaines, berges et petites routes. Relief doux.",
    watch: ["Affluence sur le littoral", "Traversée de Bayonne", "Vent côtier"],
    stops: ["Bayonne", "Mouguerre", "Urt"], retreat: ["Bayonne"],
    sleepPlan: "Demander un jardin à Urt ou dans les villages de l’Adour ; repli commercial autour de Bayonne.",
    gpx: "/gpx/01-biarritz-urt.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 2, from: "Urt", to: "Salies-de-Béarn", distanceKm: 47, officialTime: "3 h 06",
    difficulty: "habitué",
    summary: "Bidouze, chemins de halage et premières bosses béarnaises jusqu’à la cité du Sel.",
    terrain: "Chemins roulants puis petites routes vallonnées vers Came.",
    watch: ["Revêtement humide après pluie", "Montées à partir de Came", "Ravitaillement espacé"],
    stops: ["Guiche", "Bidache", "Came"], retreat: ["Peyrehorade à distance", "Orthez à l’étape suivante"],
    sleepPlan: "Réseaux d’accueil cycliste ou demande auprès des habitants autour de Salies.",
    gpx: "/gpx/02-urt-salies.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 3, from: "Salies-de-Béarn", to: "Orthez", distanceKm: 19, officialTime: "1 h 14",
    difficulty: "habitué",
    summary: "Courte liaison sur une ancienne voie ferrée, idéale pour récupérer ou regrouper deux étapes.",
    terrain: "Voie verte, tunnel éclairé, entrée pavée dans Orthez.",
    watch: ["Éclairage avant/arrière dans le tunnel", "Pavés humides à Orthez"],
    stops: ["Bellocq", "Bérenx", "Sainte-Suzanne"], retreat: ["Orthez"],
    sleepPlan: "Orthez offre le meilleur éventail de ravitaillement et de replis.",
    gpx: "/gpx/03-salies-orthez.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 4, from: "Orthez", to: "Pau", distanceKm: 55, officialTime: "3 h 38",
    difficulty: "habitué",
    summary: "Progression champêtre le long du gave de Pau avec une arrivée urbaine sereine.",
    terrain: "Routes partagées calmes et voie verte sur la fin.",
    watch: ["Chaleur dans les secteurs ouverts", "Entrées/sorties de Mourenx"],
    stops: ["Mourenx", "Tarsacq", "Arbus", "Jurançon"], retreat: ["Artix", "Pau"],
    sleepPlan: "Chercher un accueil en périphérie ouest de Pau avant d’entrer dans la zone dense.",
    gpx: "/gpx/04-orthez-pau.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 5, from: "Pau", to: "Lourdes", distanceKm: 50, officialTime: "3 h 20",
    difficulty: "habitué",
    summary: "Remontée du gave, bastides et premiers reliefs sensibles à l’approche de Lourdes.",
    terrain: "Voie verte, petites routes et courtes bosses.",
    watch: ["Affluence à Lourdes", "Orages de piémont", "Zones glissantes près du gave"],
    stops: ["Nay", "Lestelle-Bétharram", "Saint-Pé-de-Bigorre"], retreat: ["Coarraze-Nay", "Lourdes"],
    sleepPlan: "Les communes en amont de Lourdes sont plus propices à une demande de jardin.",
    gpx: "/gpx/05-pau-lourdes.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 6, from: "Lourdes", to: "Lannemezan", distanceKm: 83, officialTime: "5 h 33",
    difficulty: "aventure",
    summary: "L’étape la plus longue : Tarbes puis accumulation de collines jusqu’au plateau de Lannemezan.",
    terrain: "Routes partagées, sections rugueuses et nombreuses relances.",
    watch: ["Étape à scinder si chaleur", "Revêtement très dégradé signalé", "Trafic par endroits", "Cumul de dénivelé"],
    stops: ["Tarbes", "Tournay", "Ozon"], retreat: ["Tarbes", "Tournay", "Lannemezan"],
    sleepPlan: "Scission recommandée à Tarbes, Tournay ou Ozon ; ne pas compter sur un bivouac trouvé tard.",
    gpx: "/gpx/06-lourdes-lannemezan.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 7, from: "Lannemezan", to: "Saint-Gaudens", distanceKm: 62, officialTime: "4 h 09",
    difficulty: "habitué",
    summary: "Collines boisées, patrimoine du Comminges et arrivée le long de la Garonne.",
    terrain: "Petites routes vallonnées puis vallée plus roulante.",
    watch: ["Ravitaillement dispersé", "Chaleur sur les routes ouvertes"],
    stops: ["Montréjeau", "Saint-Bertrand-de-Comminges"], retreat: ["Lannemezan", "Montréjeau-Gourdan-Polignan", "Saint-Gaudens"],
    sleepPlan: "Demande de terrain dans les villages avant Saint-Gaudens ; nombreux replis ferroviaires.",
    gpx: "/gpx/07-lannemezan-saint-gaudens.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 8, from: "Saint-Gaudens", to: "Saint-Girons", distanceKm: 58, officialTime: "3 h 52",
    difficulty: "habitué",
    summary: "Garonne puis remontée ombragée du Salat sur une voie verte confortable.",
    terrain: "Voie verte majoritaire et petites liaisons.",
    watch: ["Sortie de Saint-Gaudens", "Balisage variable", "Humidité et feuilles sur la voie verte"],
    stops: ["Saint-Martory", "Salies-du-Salat", "Saint-Lizier"], retreat: ["Saint-Gaudens", "Boussens"],
    sleepPlan: "Nombreux villages ; demander avant d’atteindre l’agglomération de Saint-Girons.",
    gpx: "/gpx/08-saint-gaudens-saint-girons.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 9, from: "Saint-Girons", to: "Foix", distanceKm: 46, officialTime: "3 h 02",
    difficulty: "habitué",
    summary: "Ancienne voie ferrée à travers le Parc naturel régional des Pyrénées ariégeoises.",
    terrain: "Voie verte ombragée, montée douce puis descente vers Foix.",
    watch: ["Risque incendie ariégeois", "Feu et réchaud selon arrêté", "Portions isolées"],
    stops: ["La Bastide-de-Sérou", "Labouiche"], retreat: ["Foix"],
    sleepPlan: "Privilégier un accord explicite en village ; éviter les lisières forestières en période sèche.",
    gpx: "/gpx/09-saint-girons-foix.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 10, from: "Foix", to: "Mirepoix", distanceKm: 47, officialTime: "3 h 08",
    difficulty: "habitué",
    summary: "Plaine de l’Ariège, Pamiers puis vallée de l’Hers jusqu’à la bastide de Mirepoix.",
    terrain: "Routes et voies aménagées, relief contenu.",
    watch: ["Balisage signalé comme faible en quittant Foix", "Chaleur en plaine", "Trafic autour de Pamiers"],
    stops: ["Varilhes", "Pamiers"], retreat: ["Foix", "Varilhes", "Pamiers"],
    sleepPlan: "Chercher en amont de Mirepoix ; repli commercial dans la bastide.",
    gpx: "/gpx/10-foix-mirepoix.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 11, from: "Mirepoix", to: "Couiza", distanceKm: 53, officialTime: "3 h 25",
    difficulty: "habitué",
    summary: "Vallée de l’Hers, Puivert et passage de la ligne de partage des eaux.",
    terrain: "Voie verte, petites routes et col progressif.",
    watch: ["Col des Tougnets", "Services espacés après Puivert", "Orages"],
    stops: ["Chalabre", "Puivert", "Espéraza"], retreat: ["Carcassonne à distance", "Quillan", "Couiza-Montazels"],
    sleepPlan: "Chalabre et Puivert sont de bons secteurs intermédiaires pour demander un terrain.",
    gpx: "/gpx/11-mirepoix-couiza.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 12, from: "Couiza", to: "Saint-Paul-de-Fenouillet", distanceKm: 38, officialTime: "2 h 30",
    difficulty: "aventure",
    summary: "Col du Linas, point culminant de la Vélosud, puis gorges de Galamus.",
    terrain: "Montée soutenue, route spectaculaire et passages étroits.",
    watch: ["669 m au col du Linas", "Risque incendie élevé", "Gorges étroites et circulation", "Très peu d’eau"],
    stops: ["Rennes-les-Bains", "Bugarach", "Gorges de Galamus"], retreat: ["Couiza-Montazels", "Quillan"],
    sleepPlan: "Planifier la nuit avant le départ ; aucun feu, et réchaud seulement si l’autorisation du jour est certaine.",
    gpx: "/gpx/12-couiza-saint-paul.gpx", checkedAt: "2026-07-28",
  },
  {
    id: 13, from: "Saint-Paul-de-Fenouillet", to: "Le Barcarès", distanceKm: 65, officialTime: "4 h 20",
    difficulty: "habitué",
    summary: "Fenouillèdes, vignobles, Rivesaltes puis descente vers la Méditerranée.",
    terrain: "Routes, pistes et sections urbaines proches du littoral.",
    watch: ["Carte incendie 66 obligatoire", "Pistes interdites en niveau rouge", "Tramontane", "Chaleur et faible ombre"],
    stops: ["Maury", "Tautavel", "Rivesaltes"], retreat: ["Rivesaltes", "Perpignan", "Leucate-La Franqui"],
    sleepPlan: "Finir au Barcarès puis rejoindre un accueil ou un repli légal ; bivouac côtier déconseillé.",
    gpx: "/gpx/13-saint-paul-barcares.gpx", checkedAt: "2026-07-28",
  },
];

export const safetySources: SafetySource[] = [
  { id: "meteo", label: "Vigilance Météo-France", area: "Tous les départements", cadence: "Au moins 6 h et 16 h", url: "https://vigilance.meteofrance.fr/fr", critical: true },
  { id: "forest", label: "Météo des forêts", area: "France", cadence: "Chaque fin d’après-midi", url: "https://meteofrance.com/meteo-des-forets", critical: true },
  { id: "vigicrues", label: "Vigicrues", area: "Cours d’eau", cadence: "En continu", url: "https://www.vigicrues.gouv.fr/", critical: false },
  { id: "64", label: "Préfecture des Pyrénées-Atlantiques", area: "Étapes 1–5", cadence: "Selon événement", url: "https://www.pyrenees-atlantiques.gouv.fr/Actions-de-l-Etat/Agriculture-foret-et-developpement-rural/Forets/Prevention-des-incendies", critical: false },
  { id: "65", label: "Préfecture des Hautes-Pyrénées", area: "Étapes 5–7", cadence: "Selon événement", url: "https://www.hautes-pyrenees.gouv.fr/", critical: false },
  { id: "31", label: "Préfecture de la Haute-Garonne", area: "Étapes 7–8", cadence: "Selon événement", url: "https://www.haute-garonne.gouv.fr/", critical: false },
  { id: "09", label: "Actualités de la préfecture de l’Ariège", area: "Étapes 8–10", cadence: "Quotidienne si crise", url: "https://www.ariege.gouv.fr/Publications/Actualites", critical: true },
  { id: "11", label: "Préfecture de l’Aude", area: "Étapes 11–12", cadence: "Quotidienne si crise", url: "https://www.aude.gouv.fr/Actions-de-l-Etat/Environnement/Foret/Prevention-des-incendies-de-foret", critical: true },
  { id: "66", label: "Carte quotidienne incendie 66", area: "Étapes 12–13", cadence: "Vers 17–18 h pour le lendemain", url: "https://www.risque-prevention-incendie.fr/pyrenees-orientales/", critical: true },
];

export const categoryLabels: Record<ResourceCategory, { label: string; icon: string }> = {
  water: { label: "Eau potable", icon: "EAU" },
  water_unverified: { label: "Source à vérifier", icon: "?" },
  food: { label: "Manger", icon: "REP" },
  sleep: { label: "Dormir / abri", icon: "NUI" },
  repair: { label: "Réparer", icon: "VÉL" },
  train: { label: "Gare", icon: "TER" },
  health: { label: "Santé", icon: "SOS" },
  toilets: { label: "Toilettes", icon: "WC" },
  charge: { label: "Recharge", icon: "BAT" },
  info: { label: "Information", icon: "INF" },
  rest: { label: "Pause", icon: "AIR" },
};

export const hospitalityLinks = [
  { name: "Warmshowers", detail: "Hospitalité entre cyclovoyageurs — profils protégés", url: "https://www.warmshowers.org/" },
  { name: "Cyclo-Accueil-Cyclo", detail: "Réseau réciproque de Cyclo-Camping International", url: "https://www.cyclo-camping.international/cyclo-accueil-cyclo/" },
  { name: "Bikipi", detail: "Accueil gratuit entre cyclotouristes", url: "https://bikipi.com/" },
  { name: "Cycl’hôte", detail: "Réseau de la Fédération française de cyclotourisme", url: "https://ffvelo.fr/reseau-cyclhote/" },
];

export const trainRules = [
  { title: "TER Auvergne-Rhône-Alpes", rule: "Du 1er mai au 27 septembre 2026 : réservation vélo à 1 € obligatoire, dans la limite des places.", url: "https://www.ter.sncf.com/auvergne-rhone-alpes/services-contacts/voyager-avec-velo/embarquement-velo" },
  { title: "Train liO Occitanie", rule: "En juillet-août : réservation vélo gratuite, obligatoire et limitée sur les trains concernés.", url: "https://www.ter.sncf.com/occitanie/services-contacts/voyager-avec-son-velo/acces-velo-serein" },
  { title: "TER Nouvelle-Aquitaine", rule: "Du 1er mai au 27 septembre 2026 : billet vélo obligatoire sur 11 lignes ; certains VéloZEN garantissent la place.", url: "https://www.ter.sncf.com/nouvelle-aquitaine/services-contacts/voyager-avec-velo/billet-velo" },
  { title: "Intercités Toulouse–Hendaye", rule: "Vélo non démonté sur réservation, 5 € par vélo et par trajet.", url: "https://www.sncf-voyageurs.com/fr/voyagez-avec-nous/en-france/intercites/a-bord-du-train/vos-bagages/" },
  { title: "TGV INOUI", rule: "Seulement sur certains trains, réservation avec le billet ; absence de résultat = pas de place ou train incompatible.", url: "https://www.sncf-connect.com/aide/transport-de-votre-velo" },
];

export const checkItems = [
  "Vigilance météo et orages",
  "Météo des forêts",
  "Arrêtés du prochain département",
  "Trace ouverte hors ligne",
  "Eau jusqu’au prochain ravitaillement",
  "Solution de nuit + repli",
  "Batteries et éclairages",
  "Réchaud explicitement autorisé",
];

export const emergencyGuides = [
  { title: "Feu ou fumée", body: "Éloignez-vous perpendiculairement au front si possible. Rejoignez une zone dégagée ou bâtie, ne vous engagez jamais sur une piste fermée. Appelez le 112 avec route, commune et position GPS." },
  { title: "Canicule", body: "Arrêtez l’effort aux heures les plus chaudes. Ombre, refroidissement humide, petites prises d’eau répétées. Confusion, peau très chaude ou malaise : 15 ou 112." },
  { title: "Orage", body: "Quittez crêtes, arbres isolés, clôtures et cours d’eau. Écartez les vélos, accroupissez-vous pieds joints dans un creux si aucun abri solide n’est accessible." },
  { title: "Accident", body: "Sécurisez sans déplacer une personne blessée sauf danger immédiat. 112 ou 15, position GPS, nombre de victimes, état de conscience et respiration." },
  { title: "Panne majeure", body: "Quittez la chaussée, passez en mode économie d’énergie, localisez atelier ou gare. Ne poursuivez pas avec freinage, direction ou roue structurellement défaillants." },
  { title: "Plus de réseau", body: "Revenez au dernier lieu habité ou axe principal. Utilisez les fiches hors ligne et la trace GPX. Le 112 peut parfois passer via un autre réseau disponible." },
];
